// Detección de typos en el dominio del email del signup.
//
// Basado en datos reales de prod (2026-07-12, 162k usuarios): el 9.3% de las
// cuentas nunca-verificadas eran typos de un email correcto (doppelgangers), y
// el 4.3% tenía dominios sin MX (gmail.con, gamil.com, hormail.com...). El top
// de dominios reales: gmail 80.4%, familia Microsoft ~14%, yahoo ~2.4%.
//
// Estrategia en dos capas:
//   1. Mapa explícito de typos frecuentes observados en prod → corrección.
//   2. Distancia Damerau-Levenshtein ≤ umbral contra los dominios populares.
// Los dominios legítimos conocidos (KNOWN_GOOD) nunca generan sugerencia —
// protege pares reales que están "cerca" entre sí (yahoo.com vs yahoo.com.ar,
// outlook.es vs outlook.com).

// Top de dominios reales de la base (todos con MX válido). También protege:
// un email cuyo dominio está acá jamás recibe warning.
const KNOWN_GOOD: readonly string[] = [
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'hotmail.com.ar',
  'hotmail.es',
  'yahoo.com.ar',
  'yahoo.com',
  'yahoo.es',
  'icloud.com',
  'outlook.com',
  'outlook.com.ar',
  'outlook.es',
  'live.com',
  'live.com.ar',
  'msn.com',
  // Proveedores reales a distancia 1-2 de los targets — sin esto el sugeridor
  // los "corregiría" a gmail/yahoo.
  'email.com',
  'mail.com',
  'ymail.com',
  'fibertel.com.ar',
  'abc.gob.ar',
  'bue.edu.ar',
  'proton.me',
  'protonmail.com',
];

// Solo estos son destino de sugerencias (no tiene sentido sugerir un dominio
// institucional o un ISP chico por cercanía de tipeo).
const SUGGEST_TARGETS: readonly string[] = [
  'gmail.com',
  'hotmail.com',
  'hotmail.com.ar',
  'yahoo.com.ar',
  'yahoo.com',
  'icloud.com',
  'outlook.com',
  'live.com',
  'live.com.ar',
  'msn.com',
];

// Typos observados en prod que la distancia sola no ataja con confianza
// (ej: gmail.com.ar está a distancia 3 de gmail.com, pero NO existe — a
// diferencia de hotmail.com.ar / yahoo.com.ar, que sí).
const KNOWN_TYPOS: Readonly<Record<string, string>> = {
  'gmail.con': 'gmail.com',
  'gmail.com.ar': 'gmail.com',
  'gmail.comm': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmil.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'hormail.com': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmali.com': 'hotmail.com',
  'homail.com': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'icloud.con': 'icloud.com',
};

// Damerau-Levenshtein (variante OSA): inserción, borrado, sustitución y
// transposición de adyacentes cuestan 1. "gamil" → "gmail" = 1.
// Devuelve la matriz completa: la distancia es d[m][n], y el backtracking de
// domainEditScript() la recorre para reconstruir las operaciones.
function dlMatrix(a: string, b: string): number[][] {
  const m = a.length;
  const n = b.length;
  const d: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i]![j] = Math.min(
        d[i - 1]![j]! + 1, // borrado
        d[i]![j - 1]! + 1, // inserción
        d[i - 1]![j - 1]! + cost, // sustitución
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i]![j] = Math.min(d[i]![j]!, d[i - 2]![j - 2]! + 1); // transposición
      }
    }
  }
  return d;
}

function damerauLevenshtein(a: string, b: string): number {
  return dlMatrix(a, b)[a.length]![b.length]!;
}

// Operación puntual del edit script. Índices: `ai` sobre el string tipeado,
// `bi` sobre el corregido. `tr` cubre el par (ai, ai+1) ↔ (bi, bi+1).
export type EditOp =
  | { type: 'keep'; ai: number; bi: number }
  | { type: 'sub'; ai: number; bi: number }
  | { type: 'tr'; ai: number; bi: number }
  | { type: 'del'; ai: number }
  | { type: 'ins'; bi: number };

/**
 * Edit script de `from` → `to` por backtracking sobre la matriz OSA, en orden
 * de lectura. Es el guión de la animación del warning de typo: cada operación
 * tiene su gesto visual (transposición = cruce, sustitución = split-flap,
 * inserción = hueco + flap desde blanco, borrado = colapso).
 */
export function domainEditScript(from: string, to: string): EditOp[] {
  const d = dlMatrix(from, to);
  const ops: EditOp[] = [];
  let i = from.length;
  let j = to.length;
  while (i > 0 || j > 0) {
    if (
      i > 1 &&
      j > 1 &&
      from[i - 1] === to[j - 2] &&
      from[i - 2] === to[j - 1] &&
      from[i - 1] !== to[j - 1] &&
      d[i]![j] === d[i - 2]![j - 2]! + 1
    ) {
      ops.push({ type: 'tr', ai: i - 2, bi: j - 2 });
      i -= 2;
      j -= 2;
    } else if (i > 0 && j > 0 && from[i - 1] === to[j - 1] && d[i]![j] === d[i - 1]![j - 1]) {
      ops.push({ type: 'keep', ai: i - 1, bi: j - 1 });
      i--;
      j--;
    } else if (i > 0 && j > 0 && d[i]![j] === d[i - 1]![j - 1]! + 1) {
      ops.push({ type: 'sub', ai: i - 1, bi: j - 1 });
      i--;
      j--;
    } else if (i > 0 && d[i]![j] === d[i - 1]![j]! + 1) {
      ops.push({ type: 'del', ai: i - 1 });
      i--;
    } else {
      ops.push({ type: 'ins', bi: j - 1 });
      j--;
    }
  }
  return ops.reverse();
}

export interface DomainSuggestion {
  /** Dominio tal cual lo tipeó el usuario (el sospechoso de typo). */
  typedDomain: string;
  /** Dominio que sospechamos que el usuario quiso escribir. */
  suggestedDomain: string;
  /** Email completo ya corregido (local@dominio-sugerido). */
  fixedEmail: string;
}

/**
 * Si el dominio del email parece un typo de un dominio popular, devuelve la
 * sugerencia; si el dominio es legítimo conocido, no parsea, o no hay match
 * cercano, devuelve null.
 */
export function suggestEmailDomain(email: string): DomainSuggestion | null {
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf('@');
  if (at <= 0 || at === trimmed.length - 1) return null;
  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);

  if (KNOWN_GOOD.includes(domain)) return null;

  const explicit = KNOWN_TYPOS[domain];
  if (explicit) {
    return { typedDomain: domain, suggestedDomain: explicit, fixedEmail: `${local}@${explicit}` };
  }

  // Caso especial gmail (80.4% de la base real): Google solo entrega casillas
  // en gmail.com / googlemail.com. Cualquier dominio cuya primera etiqueta sea
  // exactamente "gmail" con otra terminación (gmail.co, gmail.net,
  // gmail.com.br, gmail.ar...) es un typo seguro. Solo etiqueta EXACTA: los
  // typos de la etiqueta (gamil, gmial) siguen el camino de distancia de abajo,
  // que protege a dominios legítimos cercanos como email.com.
  if (domain.split('.')[0] === 'gmail') {
    return { typedDomain: domain, suggestedDomain: 'gmail.com', fixedEmail: `${local}@gmail.com` };
  }

  // Umbral: 1 edición siempre; 2 solo en dominios largos (≥ 8 chars), donde
  // dos ediciones siguen siendo inequívocas (hotmail.com ↔ hotmaill.co).
  let best: { target: string; dist: number } | null = null;
  for (const target of SUGGEST_TARGETS) {
    const dist = damerauLevenshtein(domain, target);
    if (dist === 0) return null;
    if (!best || dist < best.dist) best = { target, dist };
  }
  if (!best) return null;
  const threshold = domain.length >= 8 ? 2 : 1;
  if (best.dist > threshold) return null;
  return {
    typedDomain: domain,
    suggestedDomain: best.target,
    fixedEmail: `${local}@${best.target}`,
  };
}
